/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from "@angular/core";
import { Subject } from "rxjs";

export interface LoaderServiceMethods {
  call: any;
  key?: string | null;
  complete: boolean;
  loadSequentially?: boolean;
  abortOnError?: boolean;
}

export interface LoaderServiceOptions {
  loadSequentially: boolean;
  abortOnError: boolean;
}

@Injectable({ providedIn: "root" })
export class LoaderService {
  private subject = new Subject();
  private methods: LoaderServiceMethods[] = [];
  private errors = false;
  private sequentialCallInProgress = false;

  public load(methods: { call: any; key?: string }[], options?: Partial<LoaderServiceOptions>): Subject<any> {
    for (const method of methods) {
      this.methods.push({
        call: method.call,
        key: method.key ?? null,
        complete: false,
        loadSequentially: options?.loadSequentially ?? false,
        abortOnError: options?.abortOnError ?? false,
      });
    }

    // If loading this batch in parallel mode, call all registered methods of the batch at once and let each report complete
    if (!options || !options.loadSequentially) {
      for (const method of methods) {
        // Execute each call in a timeout to ensure the subject is subscribed to before the call is made
        setTimeout(() => {
          method.call();
        });
      }
    } else {
      this.complete(null);
    }

    return this.subject;
  }

  public complete(completed: { call: string; key?: string } | null, error = false): void {
    let allLoadsComplete = false;
    let breakOnError = false;

    if (error) {
      this.errors = true;
    }

    if (completed) {
      for (let i = 0; i < this.methods.length; i++) {
        if (
          this.methods[i].call.toString().indexOf(`this.${completed.call}(`) !== -1 &&
          this.methods[i].key == completed.key /*Note intentional use of == */
        ) {
          if (this.methods[i].loadSequentially) {
            this.sequentialCallInProgress = false;
          }
          if (error && this.methods[i].abortOnError) {
            breakOnError = true;
          }

          this.methods.splice(i, 1);
        }
      }
    }

    // If we're not breaking upon a specific call to abort on error, determine if all loads are complete or not
    if (!breakOnError) {
      allLoadsComplete = true;

      for (const method of this.methods) {
        if (!method.complete) {
          allLoadsComplete = false;
          break;
        }
      }
    }

    if (allLoadsComplete || breakOnError) {
      if (this.errors) {
        this.subject.error(
          `LoaderService had one or more errors reported; terminating with ${allLoadsComplete ? "all loads finished." : ""}${
            breakOnError ? `abort on first error from callee '${completed?.call}'().` : ""
          }`
        );
      } else {
        this.subject.complete();
      }

      // Always reset state when finalizing the subject
      this.resetState();
    } else if (!this.sequentialCallInProgress) {
      // If all loads are not complete and an existing sequential call is not in progress, see if another sequential call can be triggered
      for (const method of this.methods) {
        if (!method.complete && method.loadSequentially) {
          this.sequentialCallInProgress = true;
          method.call();
          break;
        }
      }
    }
  }

  private resetState(): void {
    this.subject = new Subject();
    this.methods = [];
    this.errors = false;
    this.sequentialCallInProgress = false;
  }
}
